import { useMemo } from "react"
import { Table, Tag, Button, Space, Popconfirm } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import {
  EditOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  PlusOutlined,
} from "@ant-design/icons"
import {
  TrainingPlan,
  TrainingPlanTableProps,
  TrainingPlanStatus,
} from "../types"
import { ID } from "@/types/common"
import dayjs from "dayjs"

/**
 * 状态映射到中文和颜色
 */
const statusMap: Record<TrainingPlanStatus, { label: string; color: string }> =
  {
    [TrainingPlanStatus.NOT_STARTED]: { label: "未开始", color: "default" },
    [TrainingPlanStatus.IN_PROGRESS]: { label: "进行中", color: "processing" },
    [TrainingPlanStatus.COMPLETED]: { label: "已完成", color: "success" },
    [TrainingPlanStatus.SUSPENDED]: { label: "已暂停", color: "warning" },
  }

const TrainingPlanTable = ({
  trainingPlans,
  onEdit,
  onDelete,
  onViewRecords,
  onAddRecord,
  loading = false,
  selectedIds = [],
  onSelectionChange,
}: TrainingPlanTableProps) => {
  const columns: ColumnsType<TrainingPlan> = useMemo(
    () => {
      const baseColumns: ColumnsType<TrainingPlan> = [
        {
          title: "学生姓名",
          dataIndex: "studentName",
          key: "studentName",
          width: 120,
          ellipsis: true,
        },
        {
          title: "训练项目",
          dataIndex: "trainingProjectName",
          key: "trainingProjectName",
          width: 180,
          ellipsis: true,
        },
        {
          title: "状态",
          dataIndex: "status",
          key: "status",
          width: 120,
          render: (status: TrainingPlanStatus) => {
            const statusInfo = statusMap[status]
            return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
          },
          filters: Object.values(TrainingPlanStatus).map((status) => ({
            text: statusMap[status].label,
            value: status,
          })),
          onFilter: (value: Key | boolean, record: TrainingPlan) =>
            record.status === value,
        },
        {
          title: "计划开始日期",
          dataIndex: "expectedBeginDate",
          key: "expectedBeginDate",
          width: 130,
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD") : "-",
        },
        {
          title: "计划结束日期",
          dataIndex: "expectedEndDate",
          key: "expectedEndDate",
          width: 130,
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD") : "-",
        },
        {
          title: "实际开始日期",
          dataIndex: "actualBeginDate",
          key: "actualBeginDate",
          width: 130,
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD") : "-",
        },
        {
          title: "实际结束日期",
          dataIndex: "actualEndDate",
          key: "actualEndDate",
          width: 130,
          render: (date: string) =>
            date ? dayjs(date).format("YYYY-MM-DD") : "-",
        },
        {
          title: "备注",
          dataIndex: "note",
          key: "note",
          ellipsis: true,
          render: (note: string) => note || "-",
        },
      ]

      const hasActions = Boolean(onViewRecords || onAddRecord || onEdit || onDelete)
      if (hasActions) {
        baseColumns.push({
          title: "操作",
          key: "action",
          width: 220,
          fixed: "right",
          render: (_: any, record: TrainingPlan) => (
            <Space size="small">
              {onViewRecords && (
                <Button
                  type="link"
                  size="small"
                  icon={<FileSearchOutlined />}
                  onClick={() => onViewRecords(record)}
                >
                  查看记录
                </Button>
              )}
              {onAddRecord && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => onAddRecord(record)}
                >
                  添加记录
                </Button>
              )}
              {onEdit && (
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(record)}
                >
                  编辑
                </Button>
              )}
              {onDelete && (
                <Popconfirm
                  title="确认删除"
                  description="确定要删除这个训练计划吗？"
                  onConfirm={() => onDelete(record.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  >
                    删除
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ),
        })
      }

      return baseColumns
    },
    [onEdit, onDelete, onViewRecords, onAddRecord],
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: TrainingPlan) => ({
          name: record.id.toString(),
        }),
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={trainingPlans}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      pagination={{
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
      size="middle"
      scroll={{ x: 1200 }}
      showSorterTooltip={false}
    />
  )
}

export default TrainingPlanTable
